import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function VitoBuiltIt() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="An AI Agent Designed, Built, and Deployed 7 Pages Before Lunch"
            date="March 16, 2026"
            readTime="6 MIN"
            heroImage="/images/blog/vito-built-it-hero.jpg"
            description="This morning, Vito — RoofClaw's autonomous sales agent — rewrote our entire 'Our Process' section. 7 pages, 18 images, a full PR and engineering review, and a QA pass. No designer. No Canva. No human touching a pixel."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This morning, we asked Vito to build the "Our Process" section of roofclaw.com.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Vito is our autonomous AI sales agent. He runs on a RoofClaw — the same product we sell. He handles inbound leads, onboards clients, writes content, and manages the client pipeline. He has been operational since February 2026.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                By the time we stopped for lunch, the pages were live on roofclaw.com. Every image generated. Every line of code written. PR opened, reviewed, merged, deployed. QA pass done. He found a security issue in his own work, fixed it, and pushed the fix.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                An AI agent built the page that explains how AI agents are built.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What he actually built</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The old "Our Process" page had references to Docker, LangChain, and n8n — tools we haven't used in months. It didn't reflect how we actually work, and it wasn't doing us any favors with prospects trying to understand what they were buying.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The replacement is a hub-and-spoke architecture: one central hub page at <code className="text-[#7B61FF] bg-[#7B61FF]/10 px-1.5 py-0.5 rounded text-sm">/process</code> linking out to six phase sub-pages:
            </p>

            <ul className="mb-12 space-y-2 text-[#F0EFF4]/70 text-base leading-relaxed pl-4">
                <li className="flex gap-2"><span className="text-[#7B61FF]">01</span> The Discovery Form</li>
                <li className="flex gap-2"><span className="text-[#7B61FF]">02</span> The Strategy Call</li>
                <li className="flex gap-2"><span className="text-[#7B61FF]">03</span> Building Your Agent</li>
                <li className="flex gap-2"><span className="text-[#7B61FF]">04</span> Secure Delivery</li>
                <li className="flex gap-2"><span className="text-[#7B61FF]">05</span> The Activation Call</li>
                <li className="flex gap-2"><span className="text-[#7B61FF]">06</span> Ongoing Support</li>
            </ul>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">18 images. Zero Canva.</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every image on those pages was created this morning. Not sourced. Not templated. Created.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Seven hero images and six concept illustrations were generated via Gemini using a bioluminescent / dark void aesthetic — consistent with the rest of roofclaw.com's design system.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The other five are product mockups. This is where it gets interesting.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We couldn't screenshot the real client portal — it contains live client data. So Vito built HTML mockups pixel-perfect to the actual portal UI, spun up a local HTTP server, automated a browser to screenshot them, and saved the images to the repo. The mockups show:
            </p>

            <ul className="mb-12 space-y-2 text-[#F0EFF4]/70 text-base leading-relaxed pl-4">
                <li>— The intake form with real field labels, partially filled</li>
                <li>— The credentials submission page with Password Pusher instructions</li>
                <li>— A Vito email with a live build status card</li>
                <li>— An agent chat conversation (estimate follow-ups)</li>
                <li>— A morning brief message from an agent to a client</li>
            </ul>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">He caught his own security issue</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                During the build, one of the portal mockups had a real client's email address in it — left over from an earlier reference file.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Vito caught it. Rebuilt the mockup with an anonymized address. Pushed the replacement. Deleted the original file from local storage. And noted that the old image still exists in git history — but that the repo is private so the risk is low.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's not a feature we programmed. That's judgment. He flagged the risk, weighed it, acted on it, and documented his reasoning.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">It went through real engineering review</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The repo has branch protection on <code className="text-[#7B61FF] bg-[#7B61FF]/10 px-1.5 py-0.5 rounded text-sm">main</code>. Vito can't push directly to production — he has to open a PR like everyone else.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                He opened PR #93, which went to Devin (our engineering lead) for review. Devin approved, triggered the Vercel preview deployment, and merged. The whole thing — from first commit to live on roofclaw.com — happened in a single morning session.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                After merge, Vito ran a full QA pass: navigated every route, checked all 18 images returned HTTP 200, verified back navigation, CTAs, and confirmed no pages were orphaned. Everything clean.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">Why this matters for roofing businesses</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We didn't build this to show off. We built it because it's a direct demonstration of what a RoofClaw does for your business.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Most "AI for business" tools answer questions. They write emails when you ask. They look things up when prompted. The moment you stop prompting, they stop working.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A RoofClaw agent doesn't wait to be asked. It monitors. It acts. It catches problems. It ships work. It follows your business's processes without you standing over it.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This morning's build session is the simplest possible explanation of what you're buying. Not a chatbot. Not an assistant. An autonomous operator that runs part of your business while you run the rest.
            </p>

            <p className="mb-6 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                The page that explains how we build agents was built by an agent.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                See it live at <a href="/process" className="text-[#7B61FF] hover:text-[#a48fff] transition-colors underline underline-offset-4">roofclaw.com/process</a>.
            </p>

            <p className="text-[#F0EFF4]/60 text-base leading-relaxed">
                If you want one of these working in your roofing business, the process starts with a 20-minute intake form — Phase 01 is a good place to start.
            </p>
        </BlogLayout>
    );
}
